import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaUniversity } from '@react-icons/all-files/fa/FaUniversity';
import { FaBuilding } from '@react-icons/all-files/fa/FaBuilding';
import { FaCog } from '@react-icons/all-files/fa/FaCog';
import { FaHome } from '@react-icons/all-files/fa/FaHome';
import { FaPlus } from '@react-icons/all-files/fa/FaPlus';
import Breadcrumb from '../../../commons/Breadcrumb';
import PageHeader from '../../../commons/PageHeader';
import PageAlert, { usePageAlert } from '../../../commons/PageAlert';
import Confirm, { useConfirm } from '../../../commons/Confirm';
import { EditButton, DeleteButton, RenderActions } from '../../../commons/DataGridActions';
import ProfaseDataGrid from '../../../commons/Datagrid';
import { useAppDispatch, useAppSelector } from '../../../store';
import { getEntities, deleteEntity, reset } from '../../../manager/regimen.reducer';
import { EMPRESA_CONFIG_BASE, regimenEditPath, regimenNewPath } from './empresa-config-routes';

export default function RegimenPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const topRef = useRef<HTMLDivElement>(null);

  const entities = useAppSelector(s => s.regimen.entities);
  const totalItems = useAppSelector(s => s.regimen.totalItems);
  const loading = useAppSelector(s => s.regimen.loading);
  const updating = useAppSelector(s => s.regimen.updating);
  const updateSuccess = useAppSelector(s => s.regimen.updateSuccess);

  const [rows, setRows] = useState<any[]>([]);
  const [lastParams, setLastParams] = useState<any>(null);

  const { alert, showToast } = usePageAlert();
  const { confirmConfig, ask, dismiss, setConfirmLoading } = useConfirm();

  useEffect(() => {
    const msg = (location.state as any)?.successMsg;
    if (msg) { showToast('success', msg); window.history.replaceState({}, ''); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (updateSuccess) {
      dispatch(reset());
      dismiss();
      if (lastParams) dispatch(getEntities(lastParams));
      showToast('success', 'Régimen eliminado correctamente.');
    }
  }, [updateSuccess]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setConfirmLoading(updating); }, [updating]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setRows(entities ?? []); }, [entities]);

  const fetchData = (params: any) => {
    setLastParams(params);
    dispatch(getEntities(params));
  };

  const regimenLabel = (row: any) => row.valor || row.descripcion || row.codigo || `#${row.id}`;

  const renderActions = (params: any) => (
    <RenderActions>
      <EditButton onClick={() => navigate(regimenEditPath(params.row.id))} />
      <DeleteButton onClick={() => {
        ask(
          <>¿Confirmas que deseas eliminar el régimen <strong>{regimenLabel(params.row)}</strong>? Esta acción no se puede deshacer.</>,
          () => dispatch(deleteEntity(params.row.id)),
          { title: 'Eliminar régimen', confirmLabel: 'Eliminar', variant: 'danger' },
        );
        topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }} />
    </RenderActions>
  );

  const cols = [
    { field: 'id', headerName: 'ID', flex: 0.5 },
    { field: 'codigo', headerName: 'Código', flex: 0.8 },
    { field: 'descripcion', headerName: 'Descripción', flex: 2 },
    { field: 'valor', headerName: 'Valor', flex: 1.2 },
    { field: '__actions', headerName: '', flex: 1, renderCell: renderActions },
  ];

  const filters = [
    { label: 'Código', name: 'codigo' },
    { label: 'Descripción', name: 'descripcion' },
    { label: 'Valor', name: 'valor' },
  ];

  return (
    <div style={g.page} ref={topRef}>
      <Breadcrumb items={[
        { label: 'Inicio', to: '/', icon: <FaHome /> },
        { label: 'Configuración', to: '/configuracion', icon: <FaCog /> },
        { label: 'Empresa', to: EMPRESA_CONFIG_BASE, icon: <FaBuilding /> },
        { label: 'Régimen', icon: <FaUniversity /> },
      ]} />

      <PageHeader
        icon={<FaUniversity />}
        iconColor="#6366f1"
        title="Régimen"
        subtitle="Catálogo de régimen"
        action={{
          label: <><FaPlus style={{ marginRight: 6 }} />Nuevo régimen</>,
          onClick: () => navigate(regimenNewPath()),
          color: '#6366f1',
        }}
      />

      <Confirm config={confirmConfig} />
      {alert && <PageAlert alert={alert} />}

      <ProfaseDataGrid
        cols={cols}
        title=""
        filters={filters}
        fetch={fetchData}
        rowCount={totalItems}
        loading={loading}
        rows={rows}
      />
    </div>
  );
}

const g: Record<string, React.CSSProperties> = {
  page: { display: 'flex', flexDirection: 'column', gap: 24 },
};
